import dayjs from "dayjs";

import { isValidDate } from "./dateFormatter";

type SortValue = string | number | Date | null | undefined;

/**
 * Helper function to validate if a value is a valid date using dayjs.
 * @param date - The value to validate.
 * @returns True if the value is a valid date, false otherwise.
 */

/**
 * Sort strings in ascending or descending order (case-insensitive).
 * @param a - First string to compare.
 * @param b - Second string to compare.
 * @param order - Sorting order: "asc" (default) or "desc".
 * @returns Negative if a < b, positive if a > b, 0 if equal.
 */
export const sortStrings = (
  a: string | null | undefined,
  b: string | null | undefined,
  order: "asc" | "desc" = "desc",
): number => {
  const result = (a ?? "").localeCompare(b ?? "", undefined, {
    sensitivity: "base",
  });

  return order === "asc" ? result : -result;
};

/**
 * Sort numbers in ascending or descending order.
 * @param a - First number to compare.
 * @param b - Second number to compare.
 * @param order - "asc" for ascending, "desc" for descending (default: "desc").
 * @returns Negative if a < b, positive if a > b, 0 if equal.
 */
export const sortNumbers = (
  a: number | null | undefined,
  b: number | null | undefined,
  order: "asc" | "desc" = "desc",
): number => {
  const numA = a ?? 0;
  const numB = b ?? 0;
  return order === "asc" ? numA - numB : numB - numA;
};

/**
 * Sort dates in ascending or descending order.
 * @param a - First date to compare.
 * @param b - Second date to compare.
 * @param order - Sorting order ('asc' or 'desc'). Defaults to 'desc'.
 * @returns Negative if a < b (asc), positive if a > b (asc), 0 if equal.
 */
export const sortDates = (
  a: SortValue,
  b: SortValue,
  order: "asc" | "desc" = "desc",
): number => {
  if (!a || !b) return 0;

  const dateA = isValidDate(a) ? dayjs(a) : dayjs(0);
  const dateB = isValidDate(b) ? dayjs(b) : dayjs(0);

  return order === "asc" ? dateA.diff(dateB) : dateB.diff(dateA);
};

/**
 * Generic sort function.
 * @param a - First value to compare.
 * @param b - Second value to compare.
 * @param order - Sorting order: "asc" for ascending, "desc" for descending (default: "desc").
 * @returns Negative if a < b, positive if a > b, 0 if equal.
 */
export const sortGeneric = <T>(
  a: T,
  b: T,
  order: "asc" | "desc" = "desc",
): number => {
  if (a === b) return 0;

  const comparison = a < b ? -1 : 1;

  return order === "asc" ? comparison : -comparison;
};

/**
 * Sort values based on a predefined ranking array.
 * @param a - First value to compare.
 * @param b - Second value to compare.
 * @param rankedArray - Ranked array of values (low to high).
 * @param direction - "asc" or "desc" (default: "desc").
 * @returns Sorting order comparison result.
 */
export const sortByRankedValue = <T extends string | number | null | undefined>(
  a: T,
  b: T,
  rankedArray: T[],
  direction: "asc" | "desc" = "desc",
): number => {
  const rankMap = new Map<T, number>(
    rankedArray.map((value, index) => [value, index]),
  );

  const getRank = (value: T): number => {
    if (value === null || value === undefined) return Number.MAX_SAFE_INTEGER;
    return rankMap.has(value)
      ? (rankMap.get(value) as number)
      : Number.MAX_SAFE_INTEGER - 1;
  };

  const rankA = getRank(a);
  const rankB = getRank(b);

  if (rankA === rankB) return 0;
  return direction === "asc" ? rankA - rankB : rankB - rankA;
};
